import { AuiColor } from './color';
import { AuiSizeType } from './size';

const BORDER_CLASSES: Record<AuiColor, string[]> = {
    transparent: ['border-transparent', 'hover:border-transparent', 'active:border-transparent'],
    gray: ['border-gray', 'hover:border-gray-dark', 'active:border-gray-darken'],
    primary: ['border-primary', 'hover:border-primary-dark', 'active:border-primary-darken'],
    success: ['border-success', 'hover:border-success-dark', 'active:border-success-darken'],
    danger: ['border-danger', 'hover:border-danger-dark', 'active:border-danger-darken'],
    warning: ['border-warning', 'hover:border-warning-dark', 'active:border-warning-darken'],
    info: ['border-info', 'hover:border-info-dark', 'active:border-info-darken'],
    help: ['border-help', 'hover:border-help-dark', 'active:border-help-darken'],
};

const SOLID_BACKGROUND_CLASSES: Record<AuiColor, string[]> = {
    transparent: ['bg-transparent', 'hover:bg-transparent', 'active:bg-transparent'],
    gray: ['bg-gray', 'hover:bg-gray-dark', 'active:bg-gray-darken'],
    primary: ['bg-primary', 'hover:bg-primary-dark', 'active:bg-primary-darken'],
    success: ['bg-success', 'hover:bg-success-dark', 'active:bg-success-darken'],
    danger: ['bg-danger', 'hover:bg-danger-dark', 'active:bg-danger-darken'],
    warning: ['bg-warning', 'hover:bg-warning-dark', 'active:bg-warning-darken'],
    info: ['bg-info', 'hover:bg-info-dark', 'active:bg-info-darken'],
    help: ['bg-help', 'hover:bg-help-dark', 'active:bg-help-darken'],
};

const TRANSPARENT_BACKGROUND_CLASSES: Record<AuiColor, string[]> = {
    transparent: [],
    gray: ['bg-gray/[0]', 'hover:bg-gray-dark/[.1]', 'active:bg-gray-darken/[.2]'],
    primary: ['bg-primary/[0]', 'hover:bg-primary-dark/[.1]', 'active:bg-primary-darken/[.2]'],
    success: ['bg-success/[0]', 'hover:bg-success-dark/[.1]', 'active:bg-success-darken/[.2]'],
    danger: ['bg-danger/[0]', 'hover:bg-danger-dark/[.1]', 'active:bg-danger-darken/[.2]'],
    warning: ['bg-warning/[0]', 'hover:bg-warning-dark/[.1]', 'active:bg-warning-darken/[.2]'],
    info: ['bg-info/[0]', 'hover:bg-info-dark/[.1]', 'active:bg-info-darken/[.2]'],
    help: ['bg-help/[0]', 'hover:bg-help-dark/[.1]', 'active:bg-help-darken/[.2]'],
};

const DECENT_BACKGROUND_CLASSES: Record<AuiColor, string[]> = {
    transparent: [],
    gray: ['bg-gray/[.1]', 'hover:bg-gray-dark/[.2]', 'active:bg-gray-darken/[.3]'],
    primary: ['bg-primary/[.1]', 'hover:bg-primary-dark/[.2]', 'active:bg-primary-darken/[.3]'],
    success: ['bg-success/[.1]', 'hover:bg-success-dark/[.2]', 'active:bg-success-darken/[.3]'],
    danger: ['bg-danger/[.1]', 'hover:bg-danger-dark/[.2]', 'active:bg-danger-darken/[.3]'],
    warning: ['bg-warning/[.1]', 'hover:bg-warning-dark/[.2]', 'active:bg-warning-darken/[.3]'],
    info: ['bg-info/[.1]', 'hover:bg-info-dark/[.2]', 'active:bg-info-darken/[.3]'],
    help: ['bg-help/[.1]', 'hover:bg-help-dark/[.2]', 'active:bg-help-darken/[.3]'],
};

const REVERSE_TEXT_CLASSES: Record<AuiColor, string[]> = {
    transparent: [],
    gray: ['text-gray-text'],
    primary: ['text-primary-text'],
    success: ['text-success-text'],
    danger: ['text-danger-text'],
    warning: ['text-warning-text'],
    info: ['text-info-text'],
    help: ['text-help-text'],
};

const COLORED_TEXT_CLASSES: Record<AuiColor, string[]> = {
    transparent: ['text-inherit', 'hover:text-inherit', 'active:text-inherit'],
    gray: ['text-gray', 'hover:text-gray', 'active:text-gray'],
    primary: ['text-primary', 'hover:text-primary-dark', 'active:text-primary-darken'],
    success: ['text-success', 'hover:text-success-dark', 'active:text-success-darken'],
    danger: ['text-danger', 'hover:text-danger-dark', 'active:text-danger-darken'],
    warning: ['text-warning', 'hover:text-warning-dark', 'active:text-warning-darken'],
    info: ['text-info', 'hover:text-info-dark', 'active:text-info-darken'],
    help: ['text-help', 'hover:text-help-dark', 'active:text-help-darken'],
};

const FOCUS_RING_CLASSES: Record<AuiColor, string[]> = {
    transparent: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-primary'],
    gray: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-gray'],
    primary: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-primary'],
    success: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-success'],
    danger: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-danger'],
    warning: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-warning'],
    info: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-info'],
    help: ['focus:outline', 'focus:outline-2', 'focus:outline-offset-2', 'focus:outline-help'],
};

const LOADING_COLOR_CLASSES: Record<AuiColor, string[]> = {
    transparent: ['before:via-surface-background-darker', 'before:w-[200%]'],
    gray: ['before:via-gray-dark/[.8]', 'before:w-[200%]'],
    primary: ['before:via-primary-dark/[.8]', 'before:w-[200%]'],
    success: ['before:via-success-dark/[.8]', 'before:w-[200%]'],
    danger: ['before:via-danger-dark/[.8]', 'before:w-[200%]'],
    warning: ['before:via-warning-dark/[.8]', 'before:w-[200%]'],
    info: ['before:via-info-dark/[.8]', 'before:w-[200%]'],
    help: ['before:via-help-dark/[.8]', 'before:w-[200%]'],
};

const SQUARE_WIDTH_CLASSES: Record<AuiSizeType, string[]> = {
    '20': ['min-w-5'],
    '24': ['min-w-6'],
    '28': ['min-w-7'],
    '32': ['min-w-8'],
    '40': ['min-w-10'],
    '48': ['min-w-12'],
};

const FONT_SIZE_CLASSES: Record<AuiSizeType, string[]> = {
    '20': ['aui-font-regular-10'],
    '24': ['aui-font-regular-16'],
    '28': ['aui-font-regular-16'],
    '32': ['aui-font-regular-16'],
    '40': ['aui-font-regular-16'],
    '48': ['aui-font-regular-16'],
};

const PADDING_Y_CLASSES: Record<AuiSizeType, string[]> = {
    '20': ['py-[3px]'],
    '24': ['py-[1px]'],
    '28': ['py-[3px]'],
    '32': ['py-[5px]'],
    '40': ['py-[9px]'],
    '48': ['py-[13px]'],
};
const PADDING_X_CLASSES: Record<AuiSizeType, string[]> = {
    '20': ['px-2', 'gap-1.5'],
    '24': ['px-2', 'gap-1.5'],
    '28': ['px-2', 'gap-1.5'],
    '32': ['px-2', 'gap-1.5'],
    '40': ['px-4', 'gap-3'],
    '48': ['px-5', 'gap-3'],
};

type RoundedType = 'rounded-full' | 'rounded-none' | 'rounded' | 'no-border';

const ROUNDED_CLASSES: Record<RoundedType, string[]> = {
    'rounded-full': ['rounded-full', 'border'],
    'rounded-none': ['rounded-none', 'border'],
    rounded: ['rounded', 'border'],
    'no-border': ['border-0'],
};

const getAllClasses = (...classes: Record<string, string[]>[]) => {
    return classes.flatMap((c) => Object.values(c).flat());
};

export {
    BORDER_CLASSES,
    COLORED_TEXT_CLASSES,
    DECENT_BACKGROUND_CLASSES,
    FOCUS_RING_CLASSES,
    FONT_SIZE_CLASSES,
    getAllClasses,
    LOADING_COLOR_CLASSES,
    PADDING_X_CLASSES,
    PADDING_Y_CLASSES,
    REVERSE_TEXT_CLASSES,
    ROUNDED_CLASSES,
    RoundedType,
    SOLID_BACKGROUND_CLASSES,
    SQUARE_WIDTH_CLASSES,
    TRANSPARENT_BACKGROUND_CLASSES,
};
